/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  ArchiveFiltersParams,
  GetAllFiltersParams,
  GetFilterByIdPathParameters,
  TLHTTPValidationError,
  TLSavedFilterCreateRequest,
  TLSavedFilterResponse,
  TLSavedFilterUpdateRequest,
  TLSuccessResponse,
  UpdateFilterPathParameters,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary Archive Filters
 */
export const archiveFilters = (
  params: ArchiveFiltersParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSuccessResponse>(
    { url: `/v2/data-harmonization/saved-filters`, method: 'DELETE', params, signal },
    options,
  );
};

export const getArchiveFiltersQueryKey = (params?: ArchiveFiltersParams) => {
  return ['DELETE', 'v2', 'data-harmonization', 'saved-filters', ...(params ? [params] : [])] as const;
};

export const getArchiveFiltersQueryOptions = <
  TData = Awaited<ReturnType<typeof archiveFilters>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: ArchiveFiltersParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof archiveFilters>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getArchiveFiltersQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof archiveFilters>>> = ({ signal }) =>
    archiveFilters(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof archiveFilters>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ArchiveFiltersQueryResult = NonNullable<Awaited<ReturnType<typeof archiveFilters>>>;
export type ArchiveFiltersQueryError = ErrorType<TLHTTPValidationError>;

export function useArchiveFilters<
  TData = Awaited<ReturnType<typeof archiveFilters>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: ArchiveFiltersParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof archiveFilters>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof archiveFilters>>,
          TError,
          Awaited<ReturnType<typeof archiveFilters>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useArchiveFilters<
  TData = Awaited<ReturnType<typeof archiveFilters>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: ArchiveFiltersParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof archiveFilters>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof archiveFilters>>,
          TError,
          Awaited<ReturnType<typeof archiveFilters>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useArchiveFilters<
  TData = Awaited<ReturnType<typeof archiveFilters>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: ArchiveFiltersParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof archiveFilters>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Archive Filters
 */

export function useArchiveFilters<
  TData = Awaited<ReturnType<typeof archiveFilters>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: ArchiveFiltersParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof archiveFilters>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getArchiveFiltersQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Archive Filters
 */
export const prefetchArchiveFiltersQuery = async <
  TData = Awaited<ReturnType<typeof archiveFilters>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params: ArchiveFiltersParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof archiveFilters>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getArchiveFiltersQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Archive Filters
 */
export const invalidateArchiveFilters = async (
  queryClient: QueryClient,
  params: ArchiveFiltersParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getArchiveFiltersQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary Get All Filters
 */
export const getAllFilters = (
  params: GetAllFiltersParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSavedFilterResponse[]>(
    { url: `/v2/data-harmonization/saved-filters`, method: 'GET', params, signal },
    options,
  );
};

export const getGetAllFiltersQueryKey = (params?: GetAllFiltersParams) => {
  return ['v2', 'data-harmonization', 'saved-filters', ...(params ? [params] : [])] as const;
};

export const getGetAllFiltersQueryOptions = <
  TData = Awaited<ReturnType<typeof getAllFilters>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: GetAllFiltersParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllFilters>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAllFiltersQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAllFilters>>> = ({ signal }) =>
    getAllFilters(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getAllFilters>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetAllFiltersQueryResult = NonNullable<Awaited<ReturnType<typeof getAllFilters>>>;
export type GetAllFiltersQueryError = ErrorType<TLHTTPValidationError>;

export function useGetAllFilters<
  TData = Awaited<ReturnType<typeof getAllFilters>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: GetAllFiltersParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllFilters>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAllFilters>>,
          TError,
          Awaited<ReturnType<typeof getAllFilters>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAllFilters<
  TData = Awaited<ReturnType<typeof getAllFilters>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: GetAllFiltersParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllFilters>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAllFilters>>,
          TError,
          Awaited<ReturnType<typeof getAllFilters>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAllFilters<
  TData = Awaited<ReturnType<typeof getAllFilters>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: GetAllFiltersParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllFilters>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get All Filters
 */

export function useGetAllFilters<
  TData = Awaited<ReturnType<typeof getAllFilters>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: GetAllFiltersParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllFilters>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetAllFiltersQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get All Filters
 */
export const prefetchGetAllFiltersQuery = async <
  TData = Awaited<ReturnType<typeof getAllFilters>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params: GetAllFiltersParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllFilters>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetAllFiltersQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get All Filters
 */
export const invalidateGetAllFilters = async (
  queryClient: QueryClient,
  params: GetAllFiltersParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetAllFiltersQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary Create Filter
 */
export const createFilter = (
  tLSavedFilterCreateRequest: BodyType<TLSavedFilterCreateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSavedFilterResponse>(
    {
      url: `/v2/data-harmonization/saved-filters`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLSavedFilterCreateRequest,
      signal,
    },
    options,
  );
};

export const getCreateFilterQueryKey = (tLSavedFilterCreateRequest?: BodyType<TLSavedFilterCreateRequest>) => {
  return ['POST', 'v2', 'data-harmonization', 'saved-filters', tLSavedFilterCreateRequest] as const;
};

export const getCreateFilterQueryOptions = <
  TData = Awaited<ReturnType<typeof createFilter>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLSavedFilterCreateRequest: BodyType<TLSavedFilterCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createFilter>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getCreateFilterQueryKey(tLSavedFilterCreateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof createFilter>>> = ({ signal }) =>
    createFilter(tLSavedFilterCreateRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof createFilter>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type CreateFilterQueryResult = NonNullable<Awaited<ReturnType<typeof createFilter>>>;
export type CreateFilterQueryError = ErrorType<TLHTTPValidationError>;

export function useCreateFilter<
  TData = Awaited<ReturnType<typeof createFilter>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLSavedFilterCreateRequest: BodyType<TLSavedFilterCreateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof createFilter>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof createFilter>>,
          TError,
          Awaited<ReturnType<typeof createFilter>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateFilter<
  TData = Awaited<ReturnType<typeof createFilter>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLSavedFilterCreateRequest: BodyType<TLSavedFilterCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createFilter>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof createFilter>>,
          TError,
          Awaited<ReturnType<typeof createFilter>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateFilter<
  TData = Awaited<ReturnType<typeof createFilter>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLSavedFilterCreateRequest: BodyType<TLSavedFilterCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createFilter>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create Filter
 */

export function useCreateFilter<
  TData = Awaited<ReturnType<typeof createFilter>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLSavedFilterCreateRequest: BodyType<TLSavedFilterCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createFilter>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCreateFilterQueryOptions(tLSavedFilterCreateRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Create Filter
 */
export const prefetchCreateFilterQuery = async <
  TData = Awaited<ReturnType<typeof createFilter>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLSavedFilterCreateRequest: BodyType<TLSavedFilterCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createFilter>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCreateFilterQueryOptions(tLSavedFilterCreateRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Create Filter
 */
export const invalidateCreateFilter = async (
  queryClient: QueryClient,
  tLSavedFilterCreateRequest: BodyType<TLSavedFilterCreateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getCreateFilterQueryKey(tLSavedFilterCreateRequest) }, options);

  return queryClient;
};

/**
 * @summary Get Filter By Id
 */
export const getFilterById = (
  { filterId }: GetFilterByIdPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSavedFilterResponse>(
    { url: `/v2/data-harmonization/saved-filters/${filterId}`, method: 'GET', signal },
    options,
  );
};

export const getGetFilterByIdQueryKey = ({ filterId }: GetFilterByIdPathParameters) => {
  return ['v2', 'data-harmonization', 'saved-filters', filterId] as const;
};

export const getGetFilterByIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getFilterById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { filterId }: GetFilterByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFilterById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetFilterByIdQueryKey({ filterId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getFilterById>>> = ({ signal }) =>
    getFilterById({ filterId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: filterId !== null && filterId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getFilterById>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetFilterByIdQueryResult = NonNullable<Awaited<ReturnType<typeof getFilterById>>>;
export type GetFilterByIdQueryError = ErrorType<TLHTTPValidationError>;

export function useGetFilterById<
  TData = Awaited<ReturnType<typeof getFilterById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetFilterByIdPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFilterById>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getFilterById>>,
          TError,
          Awaited<ReturnType<typeof getFilterById>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetFilterById<
  TData = Awaited<ReturnType<typeof getFilterById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetFilterByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFilterById>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getFilterById>>,
          TError,
          Awaited<ReturnType<typeof getFilterById>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetFilterById<
  TData = Awaited<ReturnType<typeof getFilterById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetFilterByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFilterById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Filter By Id
 */

export function useGetFilterById<
  TData = Awaited<ReturnType<typeof getFilterById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { filterId }: GetFilterByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFilterById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetFilterByIdQueryOptions({ filterId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Filter By Id
 */
export const prefetchGetFilterByIdQuery = async <
  TData = Awaited<ReturnType<typeof getFilterById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { filterId }: GetFilterByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFilterById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetFilterByIdQueryOptions({ filterId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Filter By Id
 */
export const invalidateGetFilterById = async (
  queryClient: QueryClient,
  { filterId }: GetFilterByIdPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetFilterByIdQueryKey({ filterId }) }, options);

  return queryClient;
};

/**
 * @summary Update Filter
 */
export const updateFilter = (
  { filterId }: UpdateFilterPathParameters,
  tLSavedFilterUpdateRequest: BodyType<TLSavedFilterUpdateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSavedFilterResponse>(
    {
      url: `/v2/data-harmonization/saved-filters/${filterId}`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: tLSavedFilterUpdateRequest,
      signal,
    },
    options,
  );
};

export const getUpdateFilterQueryKey = (
  { filterId }: UpdateFilterPathParameters,
  tLSavedFilterUpdateRequest?: BodyType<TLSavedFilterUpdateRequest>,
) => {
  return ['PATCH', 'v2', 'data-harmonization', 'saved-filters', filterId, tLSavedFilterUpdateRequest] as const;
};

export const getUpdateFilterQueryOptions = <
  TData = Awaited<ReturnType<typeof updateFilter>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { filterId }: UpdateFilterPathParameters,
  tLSavedFilterUpdateRequest: BodyType<TLSavedFilterUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateFilter>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getUpdateFilterQueryKey({ filterId }, tLSavedFilterUpdateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof updateFilter>>> = ({ signal }) =>
    updateFilter({ filterId }, tLSavedFilterUpdateRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: filterId !== null && filterId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof updateFilter>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type UpdateFilterQueryResult = NonNullable<Awaited<ReturnType<typeof updateFilter>>>;
export type UpdateFilterQueryError = ErrorType<TLHTTPValidationError>;

export function useUpdateFilter<
  TData = Awaited<ReturnType<typeof updateFilter>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateFilterPathParameters,
  tLSavedFilterUpdateRequest: BodyType<TLSavedFilterUpdateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateFilter>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateFilter>>,
          TError,
          Awaited<ReturnType<typeof updateFilter>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateFilter<
  TData = Awaited<ReturnType<typeof updateFilter>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateFilterPathParameters,
  tLSavedFilterUpdateRequest: BodyType<TLSavedFilterUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateFilter>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateFilter>>,
          TError,
          Awaited<ReturnType<typeof updateFilter>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateFilter<
  TData = Awaited<ReturnType<typeof updateFilter>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateFilterPathParameters,
  tLSavedFilterUpdateRequest: BodyType<TLSavedFilterUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateFilter>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update Filter
 */

export function useUpdateFilter<
  TData = Awaited<ReturnType<typeof updateFilter>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { filterId }: UpdateFilterPathParameters,
  tLSavedFilterUpdateRequest: BodyType<TLSavedFilterUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateFilter>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUpdateFilterQueryOptions({ filterId }, tLSavedFilterUpdateRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Update Filter
 */
export const prefetchUpdateFilterQuery = async <
  TData = Awaited<ReturnType<typeof updateFilter>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { filterId }: UpdateFilterPathParameters,
  tLSavedFilterUpdateRequest: BodyType<TLSavedFilterUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateFilter>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUpdateFilterQueryOptions({ filterId }, tLSavedFilterUpdateRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Update Filter
 */
export const invalidateUpdateFilter = async (
  queryClient: QueryClient,
  { filterId }: UpdateFilterPathParameters,
  tLSavedFilterUpdateRequest: BodyType<TLSavedFilterUpdateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getUpdateFilterQueryKey({ filterId }, tLSavedFilterUpdateRequest) },
    options,
  );

  return queryClient;
};
